'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createBrowserClient } from '@supabase/ssr';
import { sendArtistWelcomeEmail, sendUserWelcomeEmail, sendLoginNotification } from '@/lib/email';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Create a persistent Supabase client with long-lived session storage
function createPersistentClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
  );
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // authReady = true once the initial session check has fully resolved.
  // No routing decisions should fire until authReady is true.
  const [authReady, setAuthReady] = useState(false);
  const supabaseRef = useRef(createPersistentClient());
  const supabase = supabaseRef.current;

  useEffect(() => {
    // Restore session immediately on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      setAuthReady(true);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      setAuthReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up — supports role, genre, bio
  const signUp = async (
    email: string,
    password: string,
    metadata: { fullName?: string; role?: string; genre?: string; bio?: string; avatarUrl?: string } = {}
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          role: metadata?.role || 'user',
          genre: metadata?.genre || '',
          bio: metadata?.bio || '',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;

    const name = metadata?.fullName || email.split('@')[0];
    if (metadata?.role === 'artist') {
      sendArtistWelcomeEmail(email, name);
    } else {
      sendUserWelcomeEmail(email, name);
    }

    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;

    if (data?.user) {
      const userId = data.user.id;
      const name = data.user.user_metadata?.full_name || email.split('@')[0];
      const loginTime = new Date().toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
      const deviceInfo =
        typeof navigator !== 'undefined'
          ? navigator.userAgent.slice(0, 80)
          : 'Unknown device';

      supabase.from('login_activity').insert({
        user_id: userId,
        user_agent: deviceInfo,
        device_info: deviceInfo.slice(0, 100),
        login_at: new Date().toISOString(),
        success: true,
      }).then(() => {});

      sendLoginNotification(email, name, loginTime, deviceInfo.slice(0, 60));
    }

    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    authReady,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    supabase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
